/**
 * Where the session lives between launches. localStorage, one key, JSON — and a tiny
 * emitter so the non-React API client can force a sign-out on a 401.
 */

import type { Session } from '~/data/types'
import { forgetHeldPictures } from '~/lib/service-worker'

const KEY = 'gymcloud.session'

type Listener = () => void

const listeners = new Set<Listener>()

export function loadSession(): Session | null {
  const raw = localStorage.getItem(KEY)
  if (raw === null) {
    return null
  }
  try {
    return JSON.parse(raw) as Session
  } catch {
    return null
  }
}

export function saveSession(session: Session): void {
  localStorage.setItem(KEY, JSON.stringify(session))
}

export function clearSession(): void {
  localStorage.removeItem(KEY)
  // The pictures the service worker is holding belong to whoever was signed in — see
  // `lib/service-worker.ts`. This is the one place a session ends, so it is the one place
  // they go.
  forgetHeldPictures()
  for (const listener of listeners) {
    listener()
  }
}

/** Fires when the session is cleared from outside React (the client's 401 handler). */
export function onSessionCleared(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
